import { ActionIcon, Anchor, Flex, Text } from '@mantine/core';
import { Link } from 'react-router-dom';
import { DealEventSource } from '@dealstack/shared/lib/backendClient';
import type { DealEventTct } from '@dealstack/shared/lib/backendClient';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n.ts';
import { DATE_FORMATS } from '@dealstack/shared/lib/format/dates/date.ts';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types';
import { createTextColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/TextColumn.tsx';
import { createDateColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/DateColumn.tsx';
import { DateRangePickerFilter } from '@dealstack/frontend/shared/components/Table/MantineTable/Filters/DateRangePickerFilter';
import {
	AsyncFilterMultiSelectProps,
	ExtendedColumnDef,
} from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import {
	dealEventMessageId,
	dealEventMessagePath,
	eventLogLinkState,
	formatActorName,
	formatDealEventName,
} from '@dealstack/frontend/app/pages/Project/DealEventLog/dealEventLog.utils.ts';
import { DealEventLogTab } from '@dealstack/frontend/app/pages/Project/DealEventLog/DealEventLog.types.ts';
import { DealEventStatusCell } from '@dealstack/frontend/app/pages/Project/DealEventLog/components/DealEventLogTable/DealEventStatusCell.tsx';
import { formatEventTypeLabel } from '@dealstack/frontend/app/pages/Project/DealEventLog/utils/dealEventLogType.utils.ts';
import { DealEventLogColumn } from '@dealstack/frontend/app/pages/Project/DealEventLog/utils/dealEventLogQuery.utils.ts';
import {
	DealEventFilterOption,
	getDealEventAccessTypeOptions,
	getDealEventCapitalTypeOptions,
	getDealEventCategoryOptions,
	getDealEventStatusOptions,
} from '@dealstack/frontend/app/pages/Project/DealEventLog/utils/dealEventLogFilterOptions.utils.ts';

type Translate = TranslateFunction<I18Namespaces.CAP_MANAGE>;
type TranslateMessages = TranslateFunction<I18Namespaces.MIP_MESSAGES>;
type TranslateCommonUi = TranslateFunction<I18Namespaces.COMMON_UI>;

export interface DealEventLogColumnOptions {
	tMessages: TranslateMessages;
	tCommonUi: TranslateCommonUi;
	dealId: string;
	listSearch: string;
	actorOptions: DealEventFilterOption[];
	actorAsync?: AsyncFilterMultiSelectProps;
	recipientOptions: DealEventFilterOption[];
	recipientAsync: AsyncFilterMultiSelectProps;
	subjectOptions: DealEventFilterOption[];
	subjectAsync: AsyncFilterMultiSelectProps;
	securityOptions: DealEventFilterOption[];
}

const getHeaders = (tab: DealEventLogTab, t: Translate) =>
	tab === DealEventLogTab.MESSAGING
		? {
				event: t('deal_event_log.table.message'),
				type: t('deal_event_log.table.channel'),
				actor: t('deal_event_log.table.sender'),
				actorFilter: t('deal_event_log.filter.placeholder.search_senders'),
			}
		: {
				event: t('deal_event_log.table.event'),
				type: t('deal_event_log.table.type'),
				actor: t('deal_event_log.table.actor'),
				actorFilter: t('deal_event_log.filter.placeholder.search_actors'),
			};

const formatChannel = (event: DealEventTct, t: Translate): string =>
	event.source === DealEventSource.EMAIL_SENT
		? t('deal_event_log.filter.channel.email')
		: event.type;

/** Options the feed can actually narrow by on this tab; an empty list leaves the column unfiltered. */
const getTypeOptions = (tab: DealEventLogTab, t: Translate): DealEventFilterOption[] => {
	switch (tab) {
		case DealEventLogTab.ALL_EVENTS:
			return getDealEventCategoryOptions(t);
		case DealEventLogTab.CAPITAL_EVENTS:
			return getDealEventCapitalTypeOptions(t);
		case DealEventLogTab.TEAM_AND_ACCESS:
			return getDealEventAccessTypeOptions(t);
		default:
			return [];
	}
};

interface EntityColumn {
	header: string;
	filterText: string;
	multiple?: boolean;
	value: (event: DealEventTct) => string;
	options?: DealEventFilterOption[];
	filterProps?: AsyncFilterMultiSelectProps;
}

const getEntityColumn = (
	tab: DealEventLogTab,
	t: Translate,
	{
		recipientOptions,
		recipientAsync,
		subjectOptions,
		subjectAsync,
		securityOptions,
	}: DealEventLogColumnOptions
): EntityColumn | undefined => {
	switch (tab) {
		case DealEventLogTab.INVESTOR_ACTIVITY:
			return {
				header: t('deal_event_log.table.participant'),
				filterText: t('deal_event_log.table.participant'),
				value: (event) => event.participant?.participantName ?? '',
			};
		case DealEventLogTab.CAPITAL_EVENTS:
			return {
				header: t('deal_event_log.table.security'),
				filterText: t('deal_event_log.filter.placeholder.all_securities'),

				value: (event) => event.capital?.securityName ?? event.holding?.securityName ?? '',
				multiple: true,
				options: securityOptions,
				filterProps: { showSearch: true },
			};
		case DealEventLogTab.TEAM_AND_ACCESS:
			return {
				header: t('deal_event_log.table.user'),
				filterText: t('deal_event_log.filter.placeholder.search_users'),
				value: (event) => event.access?.targetName ?? event.actor.displayName ?? '',
				// Read from the events, not the deal's team: a revoked member is still named here.
				options: subjectOptions,
				filterProps: subjectAsync,
			};
		case DealEventLogTab.MESSAGING:
			return {
				header: t('deal_event_log.table.recipient'),
				filterText: t('deal_event_log.filter.placeholder.search_recipients'),
				value: (event) =>
					event.message?.recipientName ?? event.message?.recipientEmail ?? '',
				options: recipientOptions,
				filterProps: recipientAsync,
			};
		default:
			return undefined;
	}
};

const multiSelectProps = (
	options: DealEventFilterOption[],
	filterProps?: AsyncFilterMultiSelectProps
) => ({
	filterVariant: 'multi-select',
	mantineFilterMultiSelectProps: { data: options, ...filterProps },
});

const singleSelectProps = (
	options: DealEventFilterOption[],
	filterProps?: AsyncFilterMultiSelectProps
) => ({
	filterVariant: 'select',
	mantineFilterSelectProps: { data: options, ...filterProps },
});

export const getDealEventLogColumns = (
	tab: DealEventLogTab,
	t: Translate,
	columnOptions: DealEventLogColumnOptions
): ExtendedColumnDef<DealEventTct>[] => {
	const headers = getHeaders(tab, t);
	const entity = getEntityColumn(tab, t, columnOptions);
	const typeOptions = getTypeOptions(tab, t);

	return [
		{
			id: 'expander',
			header: '',
			enableSorting: false,
			showFilter: false,
			minSize: 40,
			maxSize: 48,
			meta: { disableToggleVisibility: true },
			cell: ({ row }) =>
				!row.getCanExpand() ? null : (
					<Flex align="center" justify="center" w="100%">
						<ActionIcon
							variant="transparent"
							aria-label={t('deal_event_log.table.toggle_detail')}
							onClick={(clickEvent) => {
								clickEvent.stopPropagation();
								row.toggleExpanded();
							}}
						>
							<Icon
								variant={row.getIsExpanded() ? 'caret-down' : 'caret-right'}
								size="lg"
								fillStroke={true}
								color="#000"
							/>
						</ActionIcon>
					</Flex>
				),
		},
		createTextColumn<DealEventTct>({
			id: DealEventLogColumn.DESCRIPTION,
			accessorKey: 'description',
			headerText: headers.event,

			showFilter: false,
			cell: ({ row }) => {
				const heading = formatDealEventName(
					row.original,
					t,
					columnOptions.tMessages,
					columnOptions.tCommonUi
				);
				const messageId = dealEventMessageId(row.original);

				return messageId ? (
					<Anchor
						component={Link}
						to={dealEventMessagePath(columnOptions.dealId, messageId)}
						state={eventLogLinkState(columnOptions.listSearch)}
						underline="never"
						size="sm"
						fw={500}
						truncate
						style={{ color: 'inherit', cursor: 'pointer' }}
					>
						{heading}
					</Anchor>
				) : (
					<Text size="sm" fw={500} truncate>
						{heading}
					</Text>
				);
			},
		}),
		createTextColumn<DealEventTct>({
			id: DealEventLogColumn.TYPE,
			headerText: headers.type,
			filterText: t('deal_event_log.filter.placeholder.all_types'),
			showFilter: typeOptions.length > 0,
			...multiSelectProps(typeOptions, { showSearch: false }),
			accessorFn: (event) =>
				tab === DealEventLogTab.MESSAGING
					? formatChannel(event, t)
					: formatEventTypeLabel(event, t),
		}),
		createTextColumn<DealEventTct>({
			id: DealEventLogColumn.ACTOR,
			headerText: headers.actor,
			filterText: headers.actorFilter,
			showFilter: true,
			...singleSelectProps(columnOptions.actorOptions, {
				showSearch: true,
				...columnOptions.actorAsync,
			}),
			accessorFn: (event) => formatActorName(event.actor, t),
		}),
		...(entity
			? [
					createTextColumn<DealEventTct>({
						id: DealEventLogColumn.ENTITY,
						headerText: entity.header,
						filterText: entity.filterText,
						showFilter: !!entity.options,
						...(entity.multiple
							? multiSelectProps(entity.options ?? [], entity.filterProps)
							: singleSelectProps(entity.options ?? [], entity.filterProps)),
						accessorFn: entity.value,
					}),
				]
			: []),
		createTextColumn<DealEventTct>({
			id: DealEventLogColumn.STATUS,
			accessorKey: 'status',
			headerText: t('deal_event_log.table.status'),
			filterText: t('deal_event_log.filter.placeholder.all_statuses'),
			showFilter: true,
			...multiSelectProps(getDealEventStatusOptions(tab, t)),
			cell: ({ row }) => <DealEventStatusCell status={row.original.status} />,
		}),
		createDateColumn<DealEventTct>({
			id: DealEventLogColumn.OCCURRED_AT,
			accessorKey: 'occurredAt',
			headerText: t('deal_event_log.table.timestamp'),
			dateFormat: DATE_FORMATS.DATE_TIME,
			showFilter: true,
			filterValueType: 'date-range',
			filterVariant: 'date-range',
			filter: ({ column }) => (
				<DateRangePickerFilter
					column={column}
					placeholder={t('deal_event_log.filter.placeholder.all_time')}
				/>
			),
		}),
	];
};
